import { normalizeGeneralDiscountType, CURTAIN_QUOTE_TYPE } from "../components/generalQuotationUtils";
import { isAccessoryCalculationType, normalizeProductConfig, normalizeProductName } from "./productConfigs";
import { devLog } from "./devUi";
const isDevelopmentMode = () => process.env.NODE_ENV === "development";

const isCurtainAccessoryRowStatic = (row = {}) =>
  row?.lineType === "accessory" ||
  isAccessoryCalculationType(row?.calculationType || row?.productConfigSnapshot?.calculationType);

const num = (v) => {
  if (v == null || v === "") return 0;
  const cleaned = String(v)
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const n = Number.parseFloat(cleaned);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100) / 100;
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const CURTAIN_GST_RATE_OPTIONS = [0, 5, 12, 18];

const CURTAIN_GST_MODE_FLAT = "flat";

const CURTAIN_GST_MODE_FABRIC_5_OTHER_18 = "fabric_5_other_18";

const CURTAIN_MIXED_GST_FABRIC_RATE = 5;

const CURTAIN_MIXED_GST_OTHER_RATE = 18;

const normalizeCurtainGstRate = (value = 0) => {
  const numericRate = Number(value);
  return CURTAIN_GST_RATE_OPTIONS.includes(numericRate) ? numericRate : 0;
};

const normalizeCurtainGstMode = (value = CURTAIN_GST_MODE_FLAT) =>
  value === CURTAIN_GST_MODE_FABRIC_5_OTHER_18
    ? CURTAIN_GST_MODE_FABRIC_5_OTHER_18
    : CURTAIN_GST_MODE_FLAT;

const getCurtainGstModeLabel = (quoteGstMode = CURTAIN_GST_MODE_FLAT, quoteGstRate = 0) => {
  const normalizedMode = normalizeCurtainGstMode(quoteGstMode);
  if (normalizedMode === CURTAIN_GST_MODE_FABRIC_5_OTHER_18) {
    return "Fabric 5% + Other 18%";
  }
  const normalizedRate = normalizeCurtainGstRate(quoteGstRate);
  return normalizedRate > 0 ? `${normalizedRate}%` : "No GST / 0%";
};

const isFabricTaxProduct = (row = {}, productConfig = null) => {
  if (isCurtainAccessoryRowStatic(row)) return false;
  const normalizedConfig = productConfig
    ? normalizeProductConfig(productConfig)
    : normalizeProductConfig(row.productConfigSnapshot || {});
  const calculationType =
    normalizedConfig.calculationType ||
    row.calculationType ||
    row.productConfigSnapshot?.calculationType ||
    "";
  if (!["fabric_meter", "sqft_plus_fabric"].includes(calculationType)) {
    return false;
  }
  const normalizedProductName = normalizeProductName(
    row.productName ||
      row.productType ||
      row.product ||
      row.item ||
      normalizedConfig.name ||
      ""
  );
  const category = String(
    row.category || normalizedConfig.category || row.productConfigSnapshot?.category || ""
  )
    .trim()
    .toLowerCase();
  if (
    category === "blind" &&
    !normalizedProductName.includes("roman")
  ) {
    return false;
  }
  return true;
};

const getRowTaxableTotalFromCalculation = (rowCalculation = {}) =>
  round2(
    Math.max(
      0,
      num(
        rowCalculation?.taxableAmount ??
          rowCalculation?.rowSubtotalBeforeGst ??
          rowCalculation?.lineTotal ??
          rowCalculation?.rowTotal ??
          0
      )
    )
  );

const getTaxComponentsForRow = (row = {}, productConfig = null, rowCalculation = {}) => {
  const totalTaxableAmount = getRowTaxableTotalFromCalculation(rowCalculation);
  if (totalTaxableAmount <= 0) {
    return {
      fabricTaxableAmount: 0,
      otherTaxableAmount: 0,
      totalTaxableAmount: 0,
    };
  }

  if (!isFabricTaxProduct(row, productConfig || rowCalculation)) {
    return {
      fabricTaxableAmount: 0,
      otherTaxableAmount: totalTaxableAmount,
      totalTaxableAmount,
    };
  }

  const liningAmount = round2(Math.max(0, num(rowCalculation?.liningAmount)));
  const fabricPreDiscountAmount = round2(
    Math.max(
      0,
      num(
        rowCalculation?.fabricGrossAmount ??
          rowCalculation?.fabricAmountBeforeDiscount ??
          rowCalculation?.fabricAmount ??
          0
      ) + liningAmount
    )
  );
  const fabricNetAmount = round2(
    Math.max(
      0,
      num(
        rowCalculation?.fabricNetAmount ??
          rowCalculation?.fabricCost ??
          rowCalculation?.fabricAmount ??
          0
      ) + liningAmount
    )
  );
  const baseLineAmountBeforeDiscount = round2(
    Math.max(
      0,
      num(
        rowCalculation?.baseLineAmountBeforeDiscount ??
          totalTaxableAmount + num(rowCalculation?.discountAmount)
      )
    )
  );
  const discountScope = rowCalculation?.discountScope || "";
  const proportionalFabricAmount =
    discountScope === "line_total" && baseLineAmountBeforeDiscount > 0
      ? round2(fabricPreDiscountAmount * (totalTaxableAmount / baseLineAmountBeforeDiscount))
      : fabricNetAmount;
  const fabricTaxableAmount = round2(
    Math.min(totalTaxableAmount, Math.max(0, proportionalFabricAmount))
  );
  const otherTaxableAmount = round2(
    Math.max(0, totalTaxableAmount - fabricTaxableAmount)
  );

  return {
    fabricTaxableAmount,
    otherTaxableAmount,
    totalTaxableAmount,
  };
};

const calculateCanonicalQuoteDiscount = ({
  quoteType = CURTAIN_QUOTE_TYPE,
  subtotal = 0,
  discountType = "flat",
  discountValue = 0,
  source = "quote_totals",
} = {}) => {
  const safeSubtotal = Math.max(0, num(subtotal));
  const normalizedType = normalizeGeneralDiscountType(discountType);
  const rawValue = Math.max(0, num(discountValue));
  const safeValue = normalizedType === "percent" ? Math.min(rawValue, 100) : rawValue;
  const discountAmount =
    normalizedType === "percent"
      ? round2((safeSubtotal * safeValue) / 100)
      : round2(Math.min(safeValue, safeSubtotal));
  const grandTotal = round2(Math.max(0, safeSubtotal - discountAmount));

  if (isDevelopmentMode()) {
    devLog("QUOTE DISCOUNT CALC", {
      source,
      quoteType,
      subtotal: safeSubtotal,
      discountType: normalizedType,
      discountValue: safeValue,
      discountAmount,
      grandTotal,
    });
  }

  return {
    discountType: normalizedType,
    discountValue: safeValue,
    discountAmount,
    grandTotal,
  };
};

const hasExplicitCurtainRowGstRate = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const resolveCurtainAccessoryRowGstRate = (row = {}) =>
  normalizeCurtainGstRate(
    hasExplicitCurtainRowGstRate(row?.gstRate ?? row?.gst)
      ? row?.gstRate ?? row?.gst
      : 0
  );

const resolveCurtainSavedRowGstRate = (row = {}) =>
  isCurtainAccessoryRowStatic(row)
    ? resolveCurtainAccessoryRowGstRate(row)
    : "";

const calculateQuoteGstBreakdown = ({
  rows = [],
  fallbackSubtotal = 0,
  quoteGstMode = CURTAIN_GST_MODE_FLAT,
  quoteGstRate = 0,
  getRowTaxableTotal,
  getRowCalculation,
  isAccessoryRow = isCurtainAccessoryRowStatic,
} = {}) => {
  const normalizedQuoteGstMode = normalizeCurtainGstMode(quoteGstMode);
  const normalizedQuoteGstRate = normalizeCurtainGstRate(quoteGstRate);
  const rowEntries = (Array.isArray(rows) ? rows : []).map((row, index) => {
    const rowCalculation =
      typeof getRowCalculation === "function" ? getRowCalculation(row, index) : null;
    const rowTaxableCandidate =
      typeof getRowTaxableTotal === "function"
        ? getRowTaxableTotal(row, index, rowCalculation)
        : undefined;
    const rowTaxable = round2(
      Math.max(
        0,
        num(
          rowTaxableCandidate ??
            getRowTaxableTotalFromCalculation(rowCalculation || row)
        )
      )
    );
    const rowIsAccessory = Boolean(isAccessoryRow?.(row, index));
    return {
      row,
      rowCalculation: rowCalculation || {},
      taxable: rowTaxable,
      isAccessory: rowIsAccessory,
      gstRate: rowIsAccessory
        ? resolveCurtainAccessoryRowGstRate(row)
        : normalizedQuoteGstRate,
    };
  });
  const rowSubtotal = round2(
    rowEntries.reduce((sum, entry) => sum + entry.taxable, 0)
  );
  const subtotalBeforeGst = rowEntries.length
    ? rowSubtotal
    : round2(Math.max(0, num(fallbackSubtotal)));
  const measurementEntries = rowEntries.filter((entry) => !entry.isAccessory);
  const accessoryEntries = rowEntries.filter((entry) => entry.isAccessory);
  const measurementSubtotalBeforeDiscount = round2(
    measurementEntries.reduce((sum, entry) => sum + entry.taxable, 0)
  );
  const accessorySubtotalBeforeDiscount = round2(
    accessoryEntries.reduce((sum, entry) => sum + entry.taxable, 0)
  );

  if (normalizedQuoteGstMode === CURTAIN_GST_MODE_FABRIC_5_OTHER_18) {
    const fabricTaxableAmount = round2(
      rowEntries.reduce((sum, entry) => {
        const components = getTaxComponentsForRow(
          entry.row,
          entry.row.productConfigSnapshot || entry.rowCalculation,
          {
            ...entry.rowCalculation,
            taxableAmount: entry.taxable,
            rowSubtotalBeforeGst: entry.taxable,
            lineTotal: entry.taxable,
            rowTotal: entry.taxable,
          }
        );
        return sum + Number(components.fabricTaxableAmount || 0);
      }, 0)
    );
    const otherTaxableAmount = round2(
      Math.max(0, subtotalBeforeGst - fabricTaxableAmount)
    );
    const fabricGstAmount = round2(
      (fabricTaxableAmount * CURTAIN_MIXED_GST_FABRIC_RATE) / 100
    );
    const otherGstAmount = round2(
      (otherTaxableAmount * CURTAIN_MIXED_GST_OTHER_RATE) / 100
    );
    const totalGstAmount = round2(fabricGstAmount + otherGstAmount);
    const gstRates = [
      ...(fabricTaxableAmount > 0 ? [CURTAIN_MIXED_GST_FABRIC_RATE] : []),
      ...(otherTaxableAmount > 0 ? [CURTAIN_MIXED_GST_OTHER_RATE] : []),
    ];

    return {
      gstMode: normalizedQuoteGstMode,
      quoteGstMode: normalizedQuoteGstMode,
      gstModeLabel: getCurtainGstModeLabel(normalizedQuoteGstMode, normalizedQuoteGstRate),
      gstRate: 0,
      quoteGstRate: 0,
      gstRates,
      accessoryGstRates: otherTaxableAmount > 0 ? [CURTAIN_MIXED_GST_OTHER_RATE] : [],
      hasMixedGstRates: gstRates.length > 1,
      isMixedGstMode: true,
      measurementSubtotalBeforeDiscount,
      accessorySubtotalBeforeDiscount,
      taxableMeasurementAmount: measurementSubtotalBeforeDiscount,
      taxableAccessoryAmount: accessorySubtotalBeforeDiscount,
      fabricTaxableAmount,
      otherTaxableAmount,
      fabricGstAmount,
      otherGstAmount,
      measurementGstAmount: fabricGstAmount,
      accessoryGstAmount: otherGstAmount,
      taxableAmount: subtotalBeforeGst,
      subtotalBeforeGst,
      gstAmount: totalGstAmount,
      totalGstAmount,
      gstBreakdown: {
        mode: normalizedQuoteGstMode,
        quoteGstMode: normalizedQuoteGstMode,
        gstModeLabel: getCurtainGstModeLabel(normalizedQuoteGstMode, normalizedQuoteGstRate),
        fabricRate: CURTAIN_MIXED_GST_FABRIC_RATE,
        otherRate: CURTAIN_MIXED_GST_OTHER_RATE,
        fabricTaxableAmount,
        otherTaxableAmount,
        fabricGstAmount,
        otherGstAmount,
        totalGstAmount,
      },
    };
  }

  const taxableMeasurementAmount = rowEntries.length
    ? measurementSubtotalBeforeDiscount
    : subtotalBeforeGst;
  const taxableAccessoryAmount = accessorySubtotalBeforeDiscount;
  const measurementGstAmount = round2(
    (taxableMeasurementAmount * normalizedQuoteGstRate) / 100
  );
  const accessoryGstAmount = rowEntries.length
    ? round2(
        accessoryEntries.reduce(
          (sum, entry) => sum + (entry.taxable * entry.gstRate) / 100,
          0
        )
      )
    : 0;
  const totalGstAmount = round2(measurementGstAmount + accessoryGstAmount);
  const accessoryGstRates = Array.from(
    new Set(accessoryEntries.map((entry) => entry.gstRate).filter((rate) => rate > 0))
  );
  const gstRates = Array.from(
    new Set([
      ...(normalizedQuoteGstRate > 0 && taxableMeasurementAmount > 0
        ? [normalizedQuoteGstRate]
        : []),
      ...accessoryGstRates,
    ])
  );

  return {
    gstMode: normalizedQuoteGstMode,
    quoteGstMode: normalizedQuoteGstMode,
    gstModeLabel: getCurtainGstModeLabel(normalizedQuoteGstMode, normalizedQuoteGstRate),
    gstRate: normalizedQuoteGstRate,
    quoteGstRate: normalizedQuoteGstRate,
    gstRates,
    accessoryGstRates,
    hasMixedGstRates: measurementGstAmount > 0 && accessoryGstAmount > 0,
    isMixedGstMode: false,
    measurementSubtotalBeforeDiscount: taxableMeasurementAmount,
    accessorySubtotalBeforeDiscount,
    taxableMeasurementAmount,
    taxableAccessoryAmount,
    fabricTaxableAmount: 0,
    otherTaxableAmount: 0,
    fabricGstAmount: 0,
    otherGstAmount: 0,
    measurementGstAmount,
    accessoryGstAmount,
    taxableAmount: subtotalBeforeGst,
    subtotalBeforeGst,
    gstAmount: totalGstAmount,
    totalGstAmount,
    gstBreakdown: {
      mode: normalizedQuoteGstMode,
      quoteGstMode: normalizedQuoteGstMode,
      gstModeLabel: getCurtainGstModeLabel(normalizedQuoteGstMode, normalizedQuoteGstRate),
      flatRate: normalizedQuoteGstRate,
      measurementGstRate: normalizedQuoteGstRate,
      accessoryGstRates,
      taxableMeasurementAmount,
      taxableAccessoryAmount,
      measurementGstAmount,
      accessoryGstAmount,
      totalGstAmount,
    },
  };
};

const calculateCurtainQuoteTotals = ({
  rows = [],
  subtotal = 0,
  discountAmount = 0,
  discountType = "flat",
  discountValue = 0,
  quoteGstMode = CURTAIN_GST_MODE_FLAT,
  quoteGstRate = 0,
  getRowTaxableTotal,
  getRowCalculation,
  isAccessoryRow = isCurtainAccessoryRowStatic,
} = {}) => {
  const safeSubtotal = round2(Math.max(0, num(subtotal)));
  const normalizedQuoteGstMode = normalizeCurtainGstMode(quoteGstMode);
  const normalizedQuoteGstRate = normalizeCurtainGstRate(quoteGstRate);
  const resolvePostGstDiscount = (baseAmount, source = "curtain_quote_totals") => {
    const hasLegacyDiscountAmount =
      num(discountValue) <= 0 && num(discountAmount) > 0;
    if (hasLegacyDiscountAmount) {
      const safeDiscountAmount = round2(
        Math.min(Math.max(0, num(discountAmount)), Math.max(0, num(baseAmount)))
      );
      return {
        discountType: normalizeGeneralDiscountType(discountType),
        discountValue: safeDiscountAmount,
        discountAmount: safeDiscountAmount,
        grandTotal: round2(Math.max(0, num(baseAmount) - safeDiscountAmount)),
      };
    }

    return calculateCanonicalQuoteDiscount({
      quoteType: CURTAIN_QUOTE_TYPE,
      subtotal: baseAmount,
      discountType,
      discountValue,
      source,
    });
  };
  const gstBreakdown = calculateQuoteGstBreakdown({
    rows,
    fallbackSubtotal: safeSubtotal,
    quoteGstMode: normalizedQuoteGstMode,
    quoteGstRate: normalizedQuoteGstRate,
    getRowTaxableTotal,
    getRowCalculation,
    isAccessoryRow,
  });
  const measurementDiscountAmount = 0;
  const accessoryDiscountAmount = 0;
  const subtotalBeforeGst = round2(gstBreakdown.subtotalBeforeGst);
  const totalGstAmount = round2(gstBreakdown.totalGstAmount);
  const totalAfterGst = round2(Math.max(0, subtotalBeforeGst + totalGstAmount));
  const discountTotals = resolvePostGstDiscount(totalAfterGst);
  const finalTotal = discountTotals.grandTotal;

  return {
    ...gstBreakdown,
    discountType: discountTotals.discountType,
    discountValue: discountTotals.discountValue,
    discountAmount: discountTotals.discountAmount,
    discountApplication: "after_gst",
    postGstDiscountAmount: discountTotals.discountAmount,
    measurementDiscountAmount,
    accessoryDiscountAmount,
    taxableAmount: subtotalBeforeGst,
    totalAfterGst,
    finalTotal,
    grandTotal: finalTotal,
    gstBreakdown: {
      ...(gstBreakdown.gstBreakdown || {}),
      totalAfterGst,
      discountApplication: "after_gst",
      postGstDiscountAmount: discountTotals.discountAmount,
      grandTotal: finalTotal,
    },
  };
};

export { num, round2, isCurtainAccessoryRowStatic, CURTAIN_GST_RATE_OPTIONS, CURTAIN_GST_MODE_FLAT, CURTAIN_GST_MODE_FABRIC_5_OTHER_18, CURTAIN_MIXED_GST_FABRIC_RATE, CURTAIN_MIXED_GST_OTHER_RATE, normalizeCurtainGstRate, normalizeCurtainGstMode, getCurtainGstModeLabel, isFabricTaxProduct, getRowTaxableTotalFromCalculation, getTaxComponentsForRow, calculateCanonicalQuoteDiscount, hasExplicitCurtainRowGstRate, resolveCurtainAccessoryRowGstRate, resolveCurtainSavedRowGstRate, calculateQuoteGstBreakdown, calculateCurtainQuoteTotals };

